/**
 * ToastProvider.tsx
 * Système de notifications toast pour l'application
 * Gère les messages de succès, erreur, warning et info
 * Utilise Lucide React pour les icônes et Tailwind pour les styles
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';

// Types
type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastAction {
  label: string;
  action: () => void;
}

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  action?: ToastAction; // AMÉLIORATION: Action optionnelle (ex: Undo)
}

interface ToastContextValue {
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  toasts: Toast[];
  // AMÉLIORATION: Helpers pour les toasts courants
  showSuccess: (message: string, action?: ToastAction) => void;
  showError: (message: string, action?: ToastAction) => void;
  showWarning: (message: string, action?: ToastAction) => void;
  showInfo: (message: string, action?: ToastAction) => void;
}

// Context
const ToastContext = createContext<ToastContextValue | null>(null);

// Hook pour utiliser les toasts
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// Import des icônes Lucide
import { Check, X, AlertTriangle, Info } from 'lucide-react';

// Icônes pour chaque type de toast (utilise Lucide React)
const ToastIcon: React.FC<{ type: ToastType }> = ({ type }) => {
  const icons: Record<ToastType, React.ReactElement> = {
    success: <Check className="w-4 h-4" />,
    error: <X className="w-4 h-4" />,
    warning: <AlertTriangle className="w-4 h-4" />,
    info: <Info className="w-4 h-4" />,
  };
  return <span className="flex-shrink-0">{icons[type]}</span>;
};

// Composant Toast individuel (utilise Tailwind CSS)
const ToastItem: React.FC<{ toast: Toast; onClose: () => void }> = ({ toast, onClose }) => {
  const colorClasses: Record<
    ToastType,
    { bg: string; border: string; text: string; hover: string }
  > = {
    success: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-800',
      hover: 'hover:bg-green-100',
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      hover: 'hover:bg-red-100',
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-800',
      hover: 'hover:bg-amber-100',
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-800',
      hover: 'hover:bg-blue-100',
    },
  };

  const colors = colorClasses[toast.type];

  const handleAction = () => {
    if (toast.action) {
      toast.action.action();
      onClose(); // Fermer le toast après l'action
    }
  };

  return (
    <div
      className={`flex items-center gap-3 p-3 ${colors.bg} ${colors.border} border rounded-lg shadow-md mb-2 min-w-[300px] max-w-[400px] animate-in slide-in-from-right duration-300`}
    >
      <span className={`${colors.text} font-semibold flex-shrink-0`}>
        <ToastIcon type={toast.type} />
      </span>
      <div className="flex-1 min-w-0">
        <p className={`${colors.text} m-0 text-sm leading-snug`}>{toast.message}</p>
      </div>
      <div className="flex gap-2 items-center flex-shrink-0">
        {/* AMÉLIORATION: Bouton d'action (ex: Undo) */}
        {toast.action && (
          <button
            onClick={handleAction}
            className={`bg-transparent ${colors.border} border ${colors.text} cursor-pointer px-3 py-1 rounded text-xs font-medium transition-all duration-200 whitespace-nowrap ${colors.hover} hover:opacity-80`}
            aria-label={toast.action.label}
          >
            {toast.action.label}
          </button>
        )}
        <button
          onClick={onClose}
          className={`bg-transparent border-none ${colors.text} cursor-pointer p-1 opacity-70 hover:opacity-100 transition-opacity duration-200`}
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Provider
interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps): React.ReactElement {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Générer un ID unique
  const generateId = useCallback(() => {
    return `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Ajouter un toast
  const addToast = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = generateId();
      const newToast: Toast = {
        ...toast,
        id,
        duration: toast.duration ?? 5000,
      };

      setToasts(prev => [...prev, newToast]);

      // Auto-suppression après la durée spécifiée
      if (newToast.duration && newToast.duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, newToast.duration);
      }
    },
    [generateId]
  );

  // Supprimer un toast
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Écouter les erreurs globales
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      addToast({
        type: 'error',
        message: event.reason?.message || 'Une erreur inattendue est survenue',
        duration: 8000,
      });
    };

    const handleError = (event: ErrorEvent) => {
      // Éviter les erreurs de script externe
      if (event.filename) {
        addToast({
          type: 'error',
          message: event.message || 'Erreur JavaScript',
          duration: 8000,
        });
      }
    };

    // PHASE 3.4: Écouter les événements personnalisés d'ErrorHandlerService
    const handleAppError = (event: CustomEvent) => {
      const { type, message } = event.detail;
      addToast({
        type: type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'info',
        message: message || 'Une erreur est survenue',
        duration: type === 'error' ? 8000 : 5000,
      });
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);
    document.addEventListener('app-error', handleAppError as EventListener);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
      document.removeEventListener('app-error', handleAppError as EventListener);
    };
  }, [addToast]);

  // AMÉLIORATION: Helpers pour faciliter l'utilisation
  const showSuccess = useCallback(
    (message: string, action?: ToastAction) => {
      addToast({ type: 'success', message, action, duration: 3000 });
    },
    [addToast]
  );

  const showError = useCallback(
    (message: string, action?: ToastAction) => {
      addToast({ type: 'error', message, action, duration: 5000 });
    },
    [addToast]
  );

  const showWarning = useCallback(
    (message: string, action?: ToastAction) => {
      addToast({ type: 'warning', message, action, duration: 4000 });
    },
    [addToast]
  );

  const showInfo = useCallback(
    (message: string, action?: ToastAction) => {
      addToast({ type: 'info', message, action, duration: 3000 });
    },
    [addToast]
  );

  const value: ToastContextValue = {
    addToast,
    removeToast,
    toasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Container des toasts */}
      <div className="fixed top-5 right-5 z-toast max-w-[400px] w-full pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem toast={toast} onClose={() => removeToast(toast.id)} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export default ToastProvider;
